import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { useSort, sortRows, SortableTh } from "./SortableTh";
import { formatDateTime } from "../utils/datetime";

interface RbItem {
  id: string;
  name?: string;
  path?: string;
  size?: number;
  stage?: number; // 1 first-stage, 2 second-stage
  item_type?: number;
  deleted_date?: string;
  deleted_by?: string;
  author?: string;
}

type Scope =
  | { type: "sharepoint"; siteUrl: string }
  | { type: "onedrive"; userId: string };

const STAGES: { key: string; label: string }[] = [
  { key: "all", label: "All" },
  { key: "first", label: "First stage" },
  { key: "second", label: "Second stage" },
];

function fmtBytes(n?: number): string {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}
function errText(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { detail?: string } } };
  return err.response?.data?.detail || fallback;
}

/**
 * Recycle bin (first + second stage) for a SharePoint site OR a user's OneDrive.
 * Second stage = the site-collection ("admin") recycle bin. Backed by the
 * SharePoint REST API through /api/v1/m365/{cid}/{sharepoint|onedrive}/recycle-bin.
 * Multi-select → Restore or permanently Delete.
 */
export default function M365RecycleBin({
  customerId,
  scope,
}: {
  customerId: number;
  scope: Scope;
}) {
  const [stage, setStage] = useState("all");
  const [rows, setRows] = useState<RbItem[] | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const rbSort = useSort("deleted_date", "desc");

  const base =
    scope.type === "sharepoint"
      ? `/api/v1/m365/${customerId}/sharepoint/recycle-bin`
      : `/api/v1/m365/${customerId}/onedrive/${encodeURIComponent(scope.userId)}/recycle-bin`;
  const params = scope.type === "sharepoint" ? { site_url: scope.siteUrl } : {};

  const load = useCallback(
    (st: string) => {
      setLoading(true);
      setError("");
      setSel(new Set());
      setRows(null);
      api
        .get(base, { params: { ...params, stage: st } })
        .then((r) => setRows(r.data))
        .catch((e) =>
          setError(
            errText(
              e,
              "Couldn't load the recycle bin. If this tenant was connected before recycle-bin support, re-run Microsoft 365 → Tenants → Auto-connect to grant SharePoint access.",
            ),
          ),
        )
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, JSON.stringify(params)],
  );

  useEffect(() => {
    load(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const changeStage = (st: string) => {
    setStage(st);
    load(st);
  };

  const toggle = (id: string) =>
    setSel((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = !!rows && rows.length > 0 && sel.size === rows.length;
  const toggleAll = () =>
    setSel(allSelected ? new Set() : new Set((rows || []).map((r) => r.id)));

  const act = async (action: "restore" | "delete") => {
    const ids = [...sel];
    if (!ids.length) return;
    if (
      action === "delete" &&
      !window.confirm(`Permanently delete ${ids.length} item(s)? This cannot be undone.`)
    )
      return;
    setBusy(true);
    setError("");
    try {
      await api.post(`${base}/${action}`, { ids }, { params });
      load(stage);
    } catch (e) {
      setError(errText(e, `${action} failed`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-1">
          {STAGES.map((s) => (
            <button
              key={s.key}
              onClick={() => changeStage(s.key)}
              className={`text-xs px-2.5 py-1 rounded-full ${
                stage === s.key
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => act("restore")}
            disabled={busy || sel.size === 0}
            className="text-xs bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 disabled:opacity-40"
          >
            Restore{sel.size ? ` (${sel.size})` : ""}
          </button>
          <button
            onClick={() => act("delete")}
            disabled={busy || sel.size === 0}
            className="text-xs bg-red-600 text-white px-3 py-1 rounded-lg hover:bg-red-700 disabled:opacity-40"
          >
            Delete forever{sel.size ? ` (${sel.size})` : ""}
          </button>
          <button
            onClick={() => load(stage)}
            disabled={loading}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-4 py-2">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 px-4 py-6 flex items-center gap-2">
          <span className="inline-block h-3 w-3 rounded-full bg-blue-500 animate-pulse" />
          Loading recycle bin…
        </p>
      ) : error ? null : !rows || rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
          Recycle bin is empty.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <SortableTh label="Name" sortKey="name" sort={rbSort.sort} onSort={rbSort.toggle} />
                <SortableTh label="Original location" sortKey="path" sort={rbSort.sort} onSort={rbSort.toggle} />
                <SortableTh label="Deleted" sortKey="deleted_date" sort={rbSort.sort} onSort={rbSort.toggle} />
                <SortableTh label="Deleted by" sortKey="deleted_by" sort={rbSort.sort} onSort={rbSort.toggle} />
                <SortableTh label="Stage" sortKey="stage" sort={rbSort.sort} onSort={rbSort.toggle} />
                <SortableTh label="Size" sortKey="size" sort={rbSort.sort} onSort={rbSort.toggle} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(rows, rbSort.sort, (r, key) => {
                switch (key) {
                  case "path": return (r.path || "").toLowerCase();
                  case "deleted_date": return r.deleted_date ? Date.parse(r.deleted_date) : 0;
                  case "deleted_by": return (r.deleted_by || "").toLowerCase();
                  case "stage": return r.stage ?? 0;
                  case "size": return r.size ?? 0;
                  default: return (r.name || "").toLowerCase();
                }
              }).map((r) => (
                <tr
                  key={r.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/40 ${sel.has(r.id) ? "bg-blue-50/60 dark:bg-blue-900/20" : ""}`}
                >
                  <td className="px-3 py-1.5">
                    <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="px-3 py-1.5 text-gray-800 dark:text-gray-100 max-w-[16rem] truncate" title={r.name}>
                    {r.item_type === 0 ? "📁 " : "📄 "}
                    {r.name}
                  </td>
                  <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400 max-w-[16rem] truncate" title={r.path}>
                    {r.path || "—"}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {r.deleted_date ? formatDateTime(r.deleted_date) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200 max-w-[12rem] truncate" title={r.deleted_by}>
                    {r.deleted_by || "—"}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        r.stage === 2
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {r.stage === 2 ? "2nd stage" : "1st stage"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right text-gray-500 dark:text-gray-400">
                    {fmtBytes(r.size)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
